import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Stack, Typography, Divider, TextField, Drawer } from "@mui/material";
import { Chat as ChatIcon, Send as SendIcon } from "@mui/icons-material/Close";
import { faTimes, faMicrophone, faMicrophoneSlash, faVideo, faVideoSlash, faRightFromBracket, faCamera, faUpload, faRecordVinyl, faStopCircle, faDesktop, faCommentDots } from '@fortawesome/free-solid-svg-icons';
import './MeetingRoom.css';
import api from "./api/apiClient";

const MeetingRoom = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { roomId, meetingId } = useParams();
  const [isInitiator] = useState(location.state?.isInitiator ?? false);
  const { caseId } = location.state || {};

  const [isMicOn, setIsMicOn] = useState(true);
  const [isCameraOn, setIsCameraOn] = useState(true);
  const [isSharingScreen, setIsSharingScreen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [unreadMessage, setUnreadMessage] = useState(false);

  const socketRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const offerRetryRef = useRef(null);
  const hasInitializedRef = useRef(false);

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const inviteeCameraPipRef = useRef(null);
  const chatEndRef = useRef(null);

  // Local streams
  const localStreamRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const micStreamRef = useRef(null);
  const screenStreamRef = useRef(null);

  // Remote streams
  const remoteCameraStreamRef = useRef(null);
  const remoteAudioStreamRef = useRef(null);

  const mediaRecorderRef = useRef(null);
  const recordedChunks = useRef([]);

  const screenSenderRef = useRef(null);

  const remoteCameraTrackRef = useRef(null);
  const remoteScreenTrackRef = useRef(null);
  const isRenegotiatingRef = useRef(false);

  const isDraggingRef = useRef(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const inviteePipTrackRef = useRef(null);    


  useEffect(() => {
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      initMeeting();
    }
    return () => {
      cleanupMeeting();
    };
  }, []);

  const initMeeting = async () => {
    try {
      // 1️⃣ Get camera + mic
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      localStreamRef.current = stream;
      cameraStreamRef.current = new MediaStream([stream.getVideoTracks()[0]]);
      micStreamRef.current = new MediaStream([stream.getAudioTracks()[0]]);
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;

      // 2️⃣ Create RTCPeerConnection
      const pc = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });
      peerConnectionRef.current = pc;

      // Add all tracks
      stream.getTracks().forEach(track => pc.addTrack(track, stream));
      pc.ontrack = (event) => {
        const track = event.track;
      
        // AUDIO
        if (track.kind === "audio") {
          const audio = new Audio();
          audio.srcObject = new MediaStream([track]);
          audio.autoplay = true;
          audio.play().catch(() => {});
          return;
        }
      
        // VIDEO
        if (track.kind === "video") {
          const stream = event.streams[0];
      
          if (isInitiator && isSharingScreen) {
            // screen already in main, camera in PiP
            if (inviteeCameraPipRef.current) {

            }
          } else {
            if (remoteVideoRef.current) {
              remoteVideoRef.current.srcObject = stream;
            }
          }
        }


      };

      pc.onicecandidate = (event) => {
        if (event.candidate && socketRef.current?.readyState === WebSocket.OPEN) {
          sendSignal({ type: 'candidate', candidate: event.candidate });
        }
      };

      connectWebSocket();
    } catch (err) {
      console.error("Failed to initialize meeting:", err);
    }
  };

  const connectWebSocket = () => {
    socketRef.current = new WebSocket(`wss://adv.vibrantscreen.in/ws?roomId=${roomId}`);

    socketRef.current.onopen = () => {
      console.log("WebSocket connected");
      if (!isInitiator) sendSignal({ type: 'ready-to-call' });
    };

    socketRef.current.onmessage = (event) => {
      const signal = JSON.parse(event.data);
      const pc = peerConnectionRef.current;

      switch (signal.type) {
        case 'ready-to-call':
          if (isInitiator) startOfferRetry();
          break;
        case "offer":
            if (pc) {
              (async () => {
                try {
                  console.log(
                    "📨 Offer received",
                    signal.renegotiate ? "(renegotiation)" : "(initial)"
                  );
          
                  // 👇 BOTH sides can answer renegotiation offers
                  const shouldAnswer =
                    !isInitiator || signal.renegotiate === true;
          
                  if (!shouldAnswer) return;
          
                  await pc.setRemoteDescription(
                    new RTCSessionDescription(signal.offer)
                  );
          
                  const answer = await pc.createAnswer();
                  await pc.setLocalDescription(answer);
          
                  sendSignal({
                    type: "answer",
                    answer: pc.localDescription,
                    renegotiate: signal.renegotiate
                  });
          
                  console.log("📤 Answer sent");
          
                } catch (err) {
                  console.error("❌ Offer handling failed:", err);
                }
              })();
            }
            break;
                   
        case "answer":
            if (pc) {
                (async () => {
                  try {
                    await pc.setRemoteDescription(
                      new RTCSessionDescription(signal.answer)
                    );
            
                    if (signal.renegotiate) {
                      isRenegotiatingRef.current = false;
                      console.log("🔁 Renegotiation complete");
                    }else {
                      clearInterval(offerRetryRef.current);
                    }
            
                  } catch (err) {
                    console.error("❌ Error applying answer:", err);
                  }
                })();
              }
              break;
            

        case 'candidate':
          if (pc) pc.addIceCandidate(new RTCIceCandidate(signal.candidate));
          break;

        case 'chat':
          setMessages(prev => [...prev, { sender: signal.sender, text: signal.text }]);
          if (!isChatOpen) setUnreadMessage(true);
          break;

        case 'screen-share-started':
          if (isInitiator) {
            setIsSharingScreen(true);
            if (inviteeCameraPipRef.current && remoteCameraStreamRef.current) {
              inviteeCameraPipRef.current.srcObject = remoteCameraStreamRef.current;
            }
          }
          break;

        case 'screen-share-stopped':
          if (isInitiator) {
            setIsSharingScreen(false);
            if (remoteVideoRef.current && remoteCameraStreamRef.current) {
              remoteVideoRef.current.srcObject = remoteCameraStreamRef.current;
            }
            if (inviteeCameraPipRef.current) inviteeCameraPipRef.current.srcObject = null;
          }
          break;

        default:
          console.warn("Unknown signal:", signal);
      }
    };
  };

  const sendSignal = (signal) => {
    socketRef.current?.send(JSON.stringify({ roomId, ...signal }));
  };

  const createAndSendOffer = () => {
    const pc = peerConnectionRef.current;
    if (!pc) return;

    pc.createOffer().then(offer => pc.setLocalDescription(offer))
      .then(() => sendSignal({ type: 'offer', offer: pc.localDescription }));
  };

  const startOfferRetry = () => {
    offerRetryRef.current = setInterval(() => {
      createAndSendOffer();
    }, 3000);
  };
  const renegotiate = async () => {
    const pc = peerConnectionRef.current;
    if (!pc || isRenegotiatingRef.current) return;
  
    if (pc.signalingState !== "stable") {
      console.warn("⏳ PC not stable, skipping renegotiation");
      return;
    }
  
    try {
      isRenegotiatingRef.current = true;
  
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
  
      sendSignal({
        type: "offer",
        offer: pc.localDescription,
        renegotiate: true
      });
  
      console.log("🔁 Renegotiation offer sent");
    } catch (e) {
      console.error("Renegotiation failed", e);
    }
  };
  
  
  // ==============================
  // Screen Share Logic
  // ==============================
  const handleScreenShareToggle = async () => {
    const pc = peerConnectionRef.current;
    if (!pc) return;
  
    const sender = pc.getSenders().find(s => s.track?.kind === "video");
    if (!sender) return;
  
    if (!isSharingScreen) {
      const screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true });
      const screenTrack = screenStream.getVideoTracks()[0];
      screenStreamRef.current = screenStream;
  
      await sender.replaceTrack(screenTrack);
  
      screenTrack.onended = stopScreenShare;

      setIsSharingScreen(true);
      sendSignal({ type: "screen-share-started" });
    } else {
      stopScreenShare();
    }
  };


  const stopScreenShare = async () => {
    const pc = peerConnectionRef.current;
    if (!pc) return;
  
    const sender = pc.getSenders().find(s => s.track?.kind === "video");
    const cameraTrack = localStreamRef.current?.getVideoTracks()[0];
  
    if (sender && cameraTrack) {
      await sender.replaceTrack(cameraTrack);
    }
  
    screenStreamRef.current?.getTracks().forEach(t => t.stop());
    screenStreamRef.current = null;
  
    setIsSharingScreen(false);
    sendSignal({ type: "screen-share-stopped" });
  };
 

  // ==============================
  // Mic / Camera toggle
  // ==============================
  const handleToggleMic = () => {
    const localStream = localStreamRef.current;
    if (!localStream) return;
    const audioTrack = localStream.getAudioTracks()[0];
    if (audioTrack) {
      audioTrack.enabled = !isMicOn;
      setIsMicOn(!isMicOn);
    }
  };

  const handleToggleCamera = () => {
    const localStream = localStreamRef.current;
    if (!localStream) return;
    const videoTrack = localStream.getVideoTracks()[0];
    if (videoTrack) {
      videoTrack.enabled = !isCameraOn;
      setIsCameraOn(!isCameraOn);
    }
  };

  // ==============================
  // Recording
  // ==============================
  const handleToggleRecording = () => {
    const remoteStream = remoteVideoRef.current?.srcObject;
    if (!remoteStream) return;

    if (!isRecording) {
      recordedChunks.current = [];
      mediaRecorderRef.current = new MediaRecorder(remoteStream);

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) recordedChunks.current.push(e.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const blob = new Blob(recordedChunks.current, { type: "video/webm" });
        const file = new File([blob], `video_${Date.now()}.webm`, { type: "video/webm" });
        const formData = new FormData();
        formData.append("file", file);
        try {
          await api.post(`/meetingsmediacontroller/${meetingId}/video`, formData, { headers: { "Content-Type": "multipart/form-data" } });
        } catch (err) { console.error(err); }
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } else {
      mediaRecorderRef.current?.stop();
      setIsRecording(false);
    }
  };

  // ==============================
  // Logout
  // ==============================
const cleanupMeeting = () => {
  localStreamRef.current?.getTracks().forEach(t => t.stop());
  screenStreamRef.current?.getTracks().forEach(t => t.stop());
  peerConnectionRef.current?.close();
  socketRef.current?.close();
  clearInterval(offerRetryRef.current);
};

  const handleTakePhoto = async (videoRef) => {
    const video = videoRef.current;
    if (!video) {
      console.error("Video element not available for snapshot.");
      return;
    }
  
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
  
    canvas.toBlob(async (blob) => {
      if (!blob) return;
  
      const file = new File([blob], `snapshot_${Date.now()}.png`, { type: "image/png" });
      const formData = new FormData();
      formData.append("file", file);
  
      try {
        const res = await api.post(`/meetingsmediacontroller/${meetingId}/snapshot`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        console.log("✅ Snapshot uploaded:", res.data);
      } catch (err) {
        console.error("❌ Snapshot upload failed:", err);
      }
    }, "image/png");
  };

  // 📤 Upload Invitee File
  const handleUploadFile = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
  
      const formData = new FormData();
      formData.append("file", file);
  
      try {
        const res = await api.post(`/meetingsmediacontroller/upload/invitee/${meetingId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        console.log("✅ File uploaded:", res.data);
      } catch (err) {
        console.error("❌ File upload failed:", err);
      }
    };
    input.click();
  };  
  const handleLogout = async () => {
    if (isRecording) await handleToggleRecording();
    cleanupMeeting();
    if (isInitiator) navigate(`/bankverificationcasedetails/${caseId}`);
    else window.close();
  };

  // ==============================
  // Chat
  // ==============================
  const toggleChat = () => setIsChatOpen(prev => {
    const next = !prev; if (next) setUnreadMessage(false); return next;
  });

  const handleSendMessage = () => {
    if (!newMessage.trim()) return;
    sendSignal({ type: 'chat', sender: isInitiator ? 'Initiator' : 'Invitee', text: newMessage.trim() });
    setMessages(prev => [...prev, { sender: isInitiator ? 'Initiator' : 'Invitee', text: newMessage.trim() }]);
    setNewMessage('');
  };


  const handleMouseDown = (e) => {
    const video = localVideoRef.current;
    if (!video) return;
  
    isDraggingRef.current = true;
  
    const rect = video.getBoundingClientRect();
    dragOffsetRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  
    video.style.cursor = "grabbing";
  
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };
  
  const handleMouseMove = (e) => {
    if (!isDraggingRef.current) return;
  
    const video = localVideoRef.current;
    if (!video) return;
  
    const x = e.clientX - dragOffsetRef.current.x;
    const y = e.clientY - dragOffsetRef.current.y;
  
    video.style.left = `${x}px`;
    video.style.top = `${y}px`;
  };
  
  const handleMouseUp = () => {
    isDraggingRef.current = false;
  
    const video = localVideoRef.current;
    if (video) video.style.cursor = "grab";
  
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };
  

  // ==============================
  // JSX
  // ==============================
  return (
    <div className="meeting-container">
      <video ref={remoteVideoRef} className="remote-video" autoPlay playsInline />
      <video ref={localVideoRef} className="local-video" autoPlay muted playsInline onMouseDown={handleMouseDown} />
{/*      {isInitiator && isSharingScreen && (
        <video ref={inviteeCameraPipRef} className="pip-video" autoPlay playsInline />
)} */}

      <div className="meeting-id-display">Meeting ID: {roomId}</div>

      <div className="meeting-controls-panel">
      <div className="controls-group">

        {/* Mic & Camera – both */}
        <button className={`control-button ${!isMicOn ? 'off' : ''}`} onClick={handleToggleMic}>
          <FontAwesomeIcon icon={isMicOn ? faMicrophone : faMicrophoneSlash} />
        </button>

        <button className={`control-button ${!isCameraOn ? 'off' : ''}`} onClick={handleToggleCamera}>
          <FontAwesomeIcon icon={isCameraOn ? faVideo : faVideoSlash} />
        </button>

        {/* Initiator-only */}
        {isInitiator && (
          <>
            <button className="control-button" onClick={() => handleTakePhoto(remoteVideoRef)}>
              <FontAwesomeIcon icon={faCamera} />
            </button>

            <button
              className={`control-button ${isRecording ? 'recording' : ''}`}
              onClick={handleToggleRecording}
            >
              <FontAwesomeIcon icon={isRecording ? faStopCircle : faRecordVinyl} />
            </button>
          </>
        )}

        {/* Invitee-only */}
        {!isInitiator && (
          <>
            <button className="control-button" onClick={handleUploadFile}>
              <FontAwesomeIcon icon={faUpload} />
            </button>

            <button
              className={`control-button ${isSharingScreen ? 'sharing' : ''}`}
              onClick={handleScreenShareToggle}
            >
              <FontAwesomeIcon icon={faDesktop} />
            </button>
          </>
        )}
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <button className="control-button" onClick={toggleChat} title="Chat">
              <FontAwesomeIcon icon={faCommentDots} />
            </button>

            {unreadMessage && (
              <span
                style={{
                  position: 'absolute',
                  top: '6px',
                  right: '8px',
                  width: '10px',
                  height: '10px',
                  backgroundColor: 'red',
                  borderRadius: '50%',
                  animation: 'pulse 1.5s infinite',
                }}
              />
            )}
          </div>

        {/* Logout – both */}
        <button className="control-button end-call-button" onClick={handleLogout}>
          <FontAwesomeIcon icon={faRightFromBracket} />
        </button>

    </div>

      </div>

      <Drawer anchor="right" open={isChatOpen} onClose={toggleChat}>
        <Box sx={{ width: 320, p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Chat</Typography>
            <button className="control-button" onClick={toggleChat}><FontAwesomeIcon icon={faTimes} /></button>
          </Stack>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ height: '60vh', overflowY: 'auto', mb: 2 }}>
            {messages.map((msg, i) => <Typography key={i}><strong>{msg.sender}:</strong> {msg.text}</Typography>)}
            <div ref={chatEndRef}></div>
          </Box>
          <TextField fullWidth value={newMessage} onChange={(e) => setNewMessage(e.target.value)} placeholder="Type a message..." onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()} />
        </Box>
      </Drawer>
    </div>
  );
};

export default MeetingRoom;
